import React from "react";
import { Link } from "react-router-dom";
import { getAdminConstructorUrl } from "../../helpers/adminOrigin";

/**
 * Конструктор — главный экран раздела.
 *
 * Собран по гайд-инструкции «Волшебный мир», присланной Максимом 22.08.2026:
 * структура платформы (проект → локации → суперлокация), из чего состоит
 * игровое задание и какие бывают форматы.
 *
 * Списка проектов пока нет: ручки, которая отдала бы мои проекты и те, куда
 * меня добавили в команду, на платформе не существует. Показываем пустое
 * состояние, а не выдуманные карточки — иначе человек решит, что у него уже
 * есть проекты, и пойдёт их искать.
 *
 * Пока раздел собирается, работающий конструктор один — в первой версии
 * админки. Поэтому в шапке стоит переход туда: без него человек упирается в
 * страницу-описание и не понимает, где же собирать локацию на самом деле.
 */

const PUBLIC = process.env.PUBLIC_URL || "";

const ExternalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 4h6v6" />
    <path d="M20 4 11 13" />
    <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
  </svg>
);

/* Как устроена платформа — снизу вверх, от проекта к метавселенной. */
const STRUCTURE = [
  {
    key: "project",
    title: "Проект",
    note: "Объединяет все игровые материалы, настройки и созданные объекты. С него всё начинается.",
  },
  {
    key: "location",
    title: "Игровая локация",
    note: "Самостоятельная сцена внутри проекта: объекты, задания, персонажи, мультимедиа. Их может быть несколько.",
  },
  {
    key: "super",
    title: "Суперлокация",
    note: "Несколько локаций, объединённых в одно пространство — метавселенную, где игроки свободно переходят между ними.",
  },
  {
    key: "links",
    title: "Логические связи",
    note: "Соединяют локации в большой мир с единым сценарием, общим прогрессом и сквозной логикой.",
  },
];

/* Шесть форматов заданий из гайда. Порядок — от простого к сложному. */
const TASK_FORMATS = [
  { key: "code", title: "Кодовое слово", note: "Участник находит код и вписывает его в ответ." },
  { key: "quiz", title: "Викторина", note: "Выбор из вариантов; разные ответы дают разные награды." },
  { key: "moderation", title: "Модерация", note: "Участник присылает ответ или файл, его проверяет человек." },
  { key: "geo", title: "AR GEO", note: "Нужно найти 3D-артефакт, привязанный к месту на карте." },
  { key: "marker", title: "AR Marker", note: "Артефакт привязан к изображению: плакату, визитке, упаковке." },
  { key: "telegram", title: "Регистрация в Telegram", note: "Подписка или регистрация в Telegram как часть задания." },
];

/* Из чего собирается задание — параметры конструктора. */
const TASK_FIELDS = [
  "Название и картинка",
  "Описание цели и сюжета",
  "Условия взятия — что нужно, чтобы получить задание",
  "Условия прохождения — что игрок должен сделать",
  "Вознаграждение за выполнение",
  "Тип задания — формат и механика",
  "Дополнительные материалы: текст, изображения, аудио, видео",
  "Координаты — место на карте",
];

const ConstructorPage = () => (
  <div className="fw-page">
    <div className="fw-worlds-head">
      <div>
        <h1 className="fw-title">Конструктор</h1>
        <p className="fw-sub mb-0">
          Платформа создания игровых миров. Собирайте локации, задания и сценарии
          без программирования.
        </p>
      </div>
      <div className="fw-head-acts">
        <Link className="fw-btn" to={`${PUBLIC}/location-list/add`}>
          Создать проект
        </Link>
        {/* Новая вкладка, а не переход на месте: в кабинете человек обычно
            уже что-то начал, и терять это ради взгляда в старую админку он
            не должен. Вход там свой — если сессии нет, покажут форму. */}
        <a
          className="fw-btn ghost"
          href={getAdminConstructorUrl()}
          target="_blank"
          rel="noreferrer"
          /* Пересобираем адрес в момент нажатия: токен мог обновиться после
             отрисовки страницы, а с прежним админка ответит «Токен
             недействителен». Правый клик и «открыть в новой вкладке» тоже
             работают — href на месте, просто может быть чуть старее. */
          onClick={(e) => { e.currentTarget.href = getAdminConstructorUrl(); }}
        >
          <ExternalIcon />
          Перейти в первую версию админки
        </a>
      </div>
    </div>

    <div className="fw-sec-head">
      <h2>Мои проекты</h2>
    </div>
    <p className="fw-sec-sub">
      Здесь будут ваши проекты и те, куда вас добавили в команду.
    </p>

    <section className="fw-card fw-empty">
      <b>Проектов пока нет</b>
      <p>
        Создайте первый — или дождитесь, пока вас добавят в команду чужого.
        Список появится здесь, когда на платформе заработает выдача проектов.
        Уже созданные проекты и их локации пока правят в первой версии
        админки — кнопка вверху страницы.
      </p>
      <Link className="fw-btn" to={`${PUBLIC}/location-list/add`}>
        Создать проект
      </Link>
    </section>

    <div className="fw-sec-head">
      <h2>Как всё устроено</h2>
    </div>
    <p className="fw-sec-sub">
      Четыре уровня — от отдельного проекта до метавселенной.
    </p>

    <div className="fw-struct">
      {STRUCTURE.map((s, i) => (
        <section className="fw-card fw-struct-item" key={s.key}>
          <span className="n">{i + 1}</span>
          <b>{s.title}</b>
          <span>{s.note}</span>
        </section>
      ))}
    </div>

    <div className="fw-sec-head">
      <h2>Форматы игровых заданий</h2>
    </div>
    <p className="fw-sec-sub">
      Шесть механик. Их можно комбинировать в одном сценарии.
    </p>

    <div className="fw-formats">
      {TASK_FORMATS.map((f) => (
        <section className="fw-card fw-format" key={f.key}>
          <b>{f.title}</b>
          <span>{f.note}</span>
        </section>
      ))}
    </div>

    <div className="fw-sec-head">
      <h2>Из чего состоит задание</h2>
    </div>
    <p className="fw-sec-sub">
      Эти параметры вы заполняете в конструкторе, когда создаёте задание.
    </p>

    <section className="fw-card fw-box">
      <ul className="fw-extra-list">
        {TASK_FIELDS.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      <p className="fw-sec-sub mb-0">
        Подробное руководство — в Книге Знаний, когда туда переедут статьи.
      </p>
    </section>
  </div>
);

export default ConstructorPage;
